import React, { useEffect, useState } from 'react';
import { Redirect } from 'react-router-dom';
import { SerializedError } from '@reduxjs/toolkit';
import { Routes, EventIds } from 'constants/';
import { useAppSelector } from 'store/';
import { getAuthStore } from 'store/slices';
import { useCommitmentNextActionMutation } from 'store/api';
import { useNextCommitmentAction, useCommitmentTimeData } from 'hooks';
import { log } from 'utils/logService';
import { openGenericErrorNotification } from 'helpers/';
import { CommitmentActionTypes } from '../../types';

/**
 * Component that redirects users to their last action or a default route.
 * - Logged in users: fetches next commitment action and redirects accordingly
 * - Anonymous users: redirects to / (Routes.Landing)
 * Will be extended in the future to remember and redirect to last visited route.
 */
export const LastActionRerouter: React.FC = () => {
  const { isLoggedIn } = useAppSelector(getAuthStore);
  const [redirectTo, setRedirectTo] = useState<string | null>(null);
  const [getNextAction] = useCommitmentNextActionMutation();
  const handleNextAction = useNextCommitmentAction();
  const { extractCommitmentTimeData } = useCommitmentTimeData();

  useEffect(() => {
    // Only fetch next action for logged in users
    if (!isLoggedIn) {
      return;
    }

    getNextAction({
      id: '',
    }).then((result) => {
      const { error } = result as { error: SerializedError };
      if (error) {
        log.error(
          'Mutation to get next action in a commitment has failed',
          EventIds.CommitmentNextActionMutationError,
          error
        );
        openGenericErrorNotification();
        return;
      }

      // Type guard to ensure result has data property
      if (!('data' in result)) {
        return;
      }

      // Handle SupplierDetachFromCommitment or EnterStudy by redirecting to survey status
      if (
        result.data.action.type ===
          CommitmentActionTypes.SupplierDetachFromCommitment ||
        result.data.action.type === CommitmentActionTypes.EnterStudy
      ) {
        setRedirectTo(Routes.SurveyStatus);
        return;
      }

      // Extract time data from the API response
      extractCommitmentTimeData(result);
      handleNextAction(result);
    });
  }, [isLoggedIn, getNextAction, handleNextAction, extractCommitmentTimeData]);

  // Handle redirect if set by SupplierDetachFromCommitment
  if (redirectTo) {
    return <Redirect to={redirectTo} />;
  }

  // For anonymous users, redirect to landing page
  // For logged in users, return null while the API call is in progress
  // (handleNextAction will handle the navigation)
  if (!isLoggedIn) {
    return <Redirect to={Routes.Landing} />;
  }

  return null;
};
